import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Package, CheckCircle2, Shield, Scale, Factory, Users, Target,
  ClipboardList, Mail, AlertTriangle, TrendingDown,
} from 'lucide-react';
import { getAuthToken } from '../../utils/auth';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const auth = () => ({ headers: { Authorization: `Bearer ${getAuthToken()}` } });
const money = (v) => (v == null ? '—' : `$${Number(v).toLocaleString('en-US', { maximumFractionDigits: 2 })}`);

const Section = ({ icon: Icon, title, children, testId }) => (
  <div className="rounded-xl border border-white/10 bg-white/[0.02] p-5" data-testid={testId}>
    <div className="flex items-center gap-2 mb-3">
      <Icon className="w-4 h-4 text-red-300/80" />
      <span className="text-[11px] font-mono uppercase tracking-[0.14em] text-zinc-400">{title}</span>
    </div>
    {children}
  </div>
);

export const SimpleVenture = ({ projectId }) => {
  const [view, setView] = useState(null);
  const [standup, setStandup] = useState(null);
  const [scorecard, setScorecard] = useState(null);
  const [emailDraft, setEmailDraft] = useState(null);
  const [gateFail, setGateFail] = useState(null);
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API_URL}/api/idea/projects/${projectId}/simple`, auth());
      setView(r.data);
    } catch (e) {
      toast.error('Failed to load venture');
    }
  }, [projectId]);

  useEffect(() => {
    load();
    axios.get(`${API_URL}/api/idea/projects/${projectId}/standup`, auth())
      .then((r) => setStandup(r.data)).catch(() => {});
    axios.get(`${API_URL}/api/idea/projects/${projectId}/scorecard`, auth())
      .then((r) => setScorecard(r.data)).catch(() => {});
  }, [projectId, load]);

  const pick = async (part, override = false) => {
    setBusy(part.part_id);
    try {
      const body = part.top_candidate
        ? { digikey_product_number: part.top_candidate.digikey_product_number, override,
            override_reason: override ? 'owner override via simple view' : null }
        : {};
      const r = await axios.post(`${API_URL}/api/idea/projects/${projectId}/parts/${part.part_id}/pick`, body, auth());
      setGateFail(null);
      if (r.data.kind === 'quote_drafted') {
        setEmailDraft({ ...r.data.email, part_name: part.name });
        toast.success('Quote email drafted — read it below, then approve');
      } else {
        toast.success(override ? 'Overridden — recorded permanently' : `${part.name}: real price locked in`);
      }
      load();
    } catch (e) {
      const d = e.response?.data?.detail;
      if (d?.gate_failed) {
        setGateFail({ partId: part.part_id, ...d });
        toast.error('LEE stopped this pick — the part can\'t meet your spec');
      } else {
        toast.error(typeof d === 'string' ? d : 'Something went wrong');
      }
    } finally {
      setBusy(null);
    }
  };

  const openDraft = async (part) => {
    try {
      const r = await axios.get(`${API_URL}/api/actions/emails`, auth());
      const email = (r.data.emails || r.data || []).find((m) => m.id === part.rfq_email_id);
      if (email) setEmailDraft({ ...email, part_name: part.name });
      else toast.error('Draft not found');
    } catch (e) {
      toast.error('Could not open the draft');
    }
  };

  const approveDraft = async () => {
    try {
      await axios.post(`${API_URL}/api/idea/projects/${projectId}/rfqs/${emailDraft.id}/approve`, {}, auth());
      toast.success('Approved — it sends the moment email is connected (never marked sent before it truly is)');
      setEmailDraft(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Approve failed');
    }
  };

  const post = async (path, body = {}, msg = 'Done') => {
    try {
      await axios.post(`${API_URL}/api/idea/projects/${projectId}/${path}`, body, auth());
      toast.success(msg);
      load();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Failed');
    }
  };

  if (!view) return <div className="text-zinc-500 text-sm py-8">Loading venture…</div>;

  const paths = view.build_paths;
  const prot = view.protection || {};
  const econ = view.unit_economics || {};
  const wl = view.waitlist_page;
  const comp = view.competitor_scan;
  const kc = view.kill_criteria;

  return (
    <div className="space-y-5 mt-4" data-testid="simple-venture">
      <div className="text-sm text-zinc-300" data-testid="plain-status">{view.plain_status}</div>

      {/* THIS WEEK — front and center: what moved / blocked / needs YOU */}
      {standup && (
        <Section icon={Target} title={`This week · what needs you`} testId="standup">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-[12px]">
            <div><div className="text-red-300 font-mono uppercase text-[10px] mb-1">Needs you ({standup.needs_you.length})</div>{standup.needs_you.length === 0 ? <div className="text-zinc-500">Nothing — LEE is handling it</div> : standup.needs_you.map((m, i) => <div key={i} className="text-zinc-200 py-0.5">• {m}</div>)}</div>
            <div><div className="text-amber-300 font-mono uppercase text-[10px] mb-1">Blocked</div>{standup.blocked.length === 0 ? <div className="text-zinc-500">Nothing blocked</div> : standup.blocked.map((m, i) => <div key={i} className="text-zinc-400 py-0.5">• {m}</div>)}</div>
            <div><div className="text-emerald-300 font-mono uppercase text-[10px] mb-1">Moved</div>{standup.moved.length === 0 ? <div className="text-zinc-500">—</div> : standup.moved.map((m, i) => <div key={i} className="text-zinc-400 py-0.5">• {m}</div>)}</div>
          </div>
        </Section>
      )}

      {/* COST TO LAUNCH burn-down */}
      {scorecard?.cost_to_launch && (
        <Section icon={Package} title="Cost to launch" testId="cost-to-launch">
          <div className="flex items-baseline gap-3 flex-wrap">
            <span className="text-lg font-mono text-emerald-300">{money(scorecard.cost_to_launch.verified_cost)}</span>
            <span className="text-[12px] text-zinc-400">locked with real prices of</span>
            <span className="text-lg font-mono text-zinc-200">{money(scorecard.cost_to_launch.estimated_total_low)}–{money(scorecard.cost_to_launch.estimated_total_high)}</span>
            <span className="text-[12px] text-zinc-400">estimated total · {scorecard.verified_items}/{scorecard.total_items} items verified</span>
          </div>
          <div className="mt-2 h-2 rounded-full bg-white/[0.06] overflow-hidden">
            <div className="h-full bg-emerald-400/70 transition-all" style={{ width: `${Math.min(scorecard.cost_to_launch.verified_cost_pct || 0, 100)}%` }} />
          </div>
          {scorecard.cost_to_launch.next_cheapest_verification && (
            <div className="mt-2 text-[12px] text-amber-300" data-testid="next-cheapest-verification">
              Cheapest next win: verify "{scorecard.cost_to_launch.next_cheapest_verification.name}" (~{money(scorecard.cost_to_launch.next_cheapest_verification.est_unit_cost_low)}) — {scorecard.cost_to_launch.next_cheapest_verification.suggested_action}
            </div>
          )}
        </Section>
      )}

      {/* PARTS LIST */}
      <Section icon={Package} title={`Parts list · ${view.parts.length}`} testId="simple-parts">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {view.parts.map((p) => (
            <div key={p.part_id} data-testid={`part-card-${p.part_id}`}
              className={`rounded-lg border p-3 flex flex-col gap-2 ${p.state === 'priced' ? 'border-emerald-400/25 bg-emerald-500/[0.04]' : 'border-white/10 bg-white/[0.02]'}`}>
              <div className="flex items-center gap-3">
                {p.image ? (
                  <img src={p.image} alt={p.name} className="w-12 h-12 object-contain rounded bg-white/90 p-1" />
                ) : (
                  <div className="w-12 h-12 rounded bg-white/5 flex items-center justify-center">
                    <Package className="w-5 h-5 text-zinc-600" />
                  </div>
                )}
                <div className="min-w-0">
                  <div className="text-sm text-zinc-200 truncate">{p.name}</div>
                  <div className="text-[11px] text-zinc-500">
                    qty {p.qty}
                    {p.state === 'priced' && <span className="text-emerald-300"> · {money(p.price)} real price{p.vendor ? ` · ${p.vendor}` : ''}</span>}
                    {p.state === 'pick' && p.price != null && <span> · {money(p.price)} live price</span>}
                    {p.overridden && <span className="text-amber-400"> · override recorded</span>}
                  </div>
                </div>
              </div>
              {p.state === 'priced' && (
                <div className="flex items-center gap-1 text-[11px] text-emerald-300"><CheckCircle2 className="w-3.5 h-3.5" /> Locked in</div>
              )}
              {p.state === 'pick' && (
                <button data-testid={`pick-btn-${p.part_id}`} disabled={busy === p.part_id}
                  onClick={() => pick(p)}
                  className="text-[11px] font-mono uppercase px-2 py-1.5 rounded bg-emerald-500/15 text-emerald-300 hover:bg-emerald-500/25 disabled:opacity-50">
                  {busy === p.part_id ? 'working…' : 'Use this price'}
                </button>
              )}
              {p.state === 'quote' && (
                <button data-testid={`quote-btn-simple-${p.part_id}`} disabled={busy === p.part_id}
                  onClick={() => pick(p)}
                  className="text-[11px] font-mono uppercase px-2 py-1.5 rounded bg-amber-500/15 text-amber-300 hover:bg-amber-500/25 disabled:opacity-50">
                  {busy === p.part_id ? 'drafting…' : 'Ask for quote'}
                </button>
              )}
              {p.state === 'quote_draft' && (
                <button data-testid={`read-draft-btn-${p.part_id}`} onClick={() => openDraft(p)}
                  className="text-[11px] font-mono uppercase px-2 py-1.5 rounded bg-sky-500/15 text-sky-300 hover:bg-sky-500/25">
                  Read & approve email
                </button>
              )}
              {(p.state === 'quote_approved' || p.state === 'quote_queued') && (
                <div className="text-[11px] text-sky-300">Quote request approved — sends when email is connected</div>
              )}
              {gateFail && gateFail.partId === p.part_id && (
                <div className="rounded border border-red-400/30 bg-red-500/5 p-2 space-y-1" data-testid={`simple-gate-${p.part_id}`}>
                  <div className="text-[11px] text-red-300 flex items-start gap-1"><AlertTriangle className="w-3 h-3 mt-0.5 shrink-0" />{gateFail.reason}</div>
                  {(gateFail.alternatives || []).length > 0 && (
                    <div className="text-[11px] text-zinc-400">Better: {gateFail.alternatives.map((a) => `${a.manufacturer_product_number} (${money(a.unit_price)})`).join(' · ')}</div>
                  )}
                  <button onClick={() => pick(p, true)} className="text-[10px] font-mono uppercase text-red-300/80 underline">override anyway (recorded)</button>
                </div>
              )}
            </div>
          ))}
        </div>
        {(view.lee_handles || []).length > 0 && (
          <div className="text-[11px] text-zinc-500 mt-3" data-testid="lee-handles">
            LEE also plans: {view.lee_handles.map((h) => h.name).join(' · ')}
          </div>
        )}
      </Section>

      {/* EMAIL DRAWER */}
      {emailDraft && (
        <Section icon={Mail} title={`Quote request — ${emailDraft.part_name || ''}`} testId="email-drawer">
          <div className="text-sm text-white mb-1">{emailDraft.subject}</div>
          <pre className="text-[12px] text-zinc-300 whitespace-pre-wrap max-h-64 overflow-y-auto bg-black/20 rounded p-3">{emailDraft.body}</pre>
          <div className="flex gap-3 mt-3">
            <button data-testid="approve-email-btn" onClick={approveDraft}
              className="text-[11px] font-mono uppercase px-3 py-2 rounded bg-emerald-500/20 text-emerald-300 hover:bg-emerald-500/30">
              Approve & send
            </button>
            <button onClick={() => setEmailDraft(null)} className="text-[11px] font-mono uppercase px-3 py-2 rounded bg-white/5 text-zinc-400">Close</button>
          </div>
        </Section>
      )}

      {/* TWO WAYS TO BUILD */}
      <Section icon={Factory} title="Two ways to build it" testId="build-paths">
        {!paths ? (
          <button data-testid="generate-paths-btn"
            onClick={() => post('build-paths/generate', {}, 'Both build paths ready — compare below')}
            className="text-[11px] font-mono uppercase px-3 py-2 rounded bg-red-500/15 text-red-300 hover:bg-red-500/25">
            Compare build paths
          </button>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {['turnkey', 'piece_by_piece'].map((key) => {
              const pth = paths[key];
              const chosen = paths.chosen === key;
              return (
                <div key={key} data-testid={`path-${key}`}
                  className={`rounded-lg border p-4 ${chosen ? 'border-emerald-400/40 bg-emerald-500/[0.05]' : 'border-white/10'}`}>
                  <div className="text-sm text-white font-semibold mb-2">{pth.label}{chosen && <span className="text-emerald-300 text-[11px] ml-2">✓ your choice</span>}</div>
                  <div className="text-[12px] text-zinc-400 space-y-1.5">
                    <div><span className="text-zinc-500">Cost:</span> {pth.est_unit_cost != null ? `${money(pth.est_unit_cost)}/unit` : 'quotes needed'} — {pth.cost_note}</div>
                    <div><span className="text-zinc-500">Speed:</span> {pth.speed}</div>
                    <div><span className="text-zinc-500">Your effort:</span> {pth.your_effort}</div>
                    {pth.options && (
                      <div className="pt-1">
                        {pth.options.map((o) => (
                          <div key={o.name} className="text-[11px] text-zinc-400">• <span className="text-zinc-200">{o.name}</span> ({o.region}) — {o.fit}</div>
                        ))}
                      </div>
                    )}
                  </div>
                  {!chosen && (
                    <button data-testid={`choose-${key}-btn`}
                      onClick={() => post('build-paths/choose', { path: key }, 'Path chosen — LEE set up the coordination plan')}
                      className="mt-3 text-[11px] font-mono uppercase px-3 py-1.5 rounded bg-white/10 text-white hover:bg-white/20">
                      Choose this path
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </Section>

      {/* LEE COORDINATES */}
      {view.coordination && (
        <Section icon={Users} title="LEE coordinates — you never manage the middle" testId="coordination">
          <div className="space-y-1.5">
            {view.coordination.assignments.map((a, i) => (
              <div key={i} className="text-[12px] text-zinc-300 flex flex-wrap gap-1">
                <span className="text-white font-semibold">{a.who}:</span> {a.does}
                <span className="text-zinc-500">[{a.status}]</span>
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* PROTECT THE IDEA */}
      {prot.research && (
        <Section icon={Shield} title={`Protect the idea ${prot.disclosure_hold ? '· OUTREACH ON HOLD' : '· hold lifted'}`} testId="protection">
          <div className="text-[12px] text-zinc-300 space-y-1.5">
            <div>Provisional patent, file it yourself: <span className="text-emerald-300 font-mono">{prot.research.micro_entity_fee}</span></div>
            <div>{prot.research.covers}</div>
            <div className="text-zinc-500">With help: agent {prot.research.attorney_cost}</div>
            <details className="mt-2">
              <summary className="cursor-pointer text-red-300/80 text-[11px] font-mono uppercase">Read the invention description LEE drafted</summary>
              <pre className="text-[11px] text-zinc-400 whitespace-pre-wrap mt-2 bg-black/20 rounded p-3 max-h-64 overflow-y-auto">{prot.invention_description_draft}</pre>
            </details>
          </div>
          {prot.decision ? (
            <div className="text-[12px] text-emerald-300 mt-3" data-testid="protection-decision">Your decision: {prot.decision.replace(/_/g, ' ')}{prot.disclosure_hold ? ' — outreach stays held until filed' : ''}</div>
          ) : (
            <div className="flex flex-wrap gap-2 mt-3">
              {[['file_diy', 'File it myself ($65)'], ['file_attorney', 'Use an attorney'], ['filed', 'Already filed — lift hold'], ['skip_protection', 'Skip protection — lift hold']].map(([d, label]) => (
                <button key={d} data-testid={`protect-${d}-btn`}
                  onClick={() => post('protection/decide', { decision: d }, 'Decision recorded')}
                  className="text-[11px] font-mono uppercase px-3 py-1.5 rounded bg-white/10 text-white hover:bg-white/20">
                  {label}
                </button>
              ))}
            </div>
          )}
        </Section>
      )}

      {/* THE MATH */}
      {econ.target_retail && (
        <Section icon={Scale} title="The math (reverse from your price tag)" testId="unit-economics">
          <div className="text-[12px] text-zinc-300 space-y-1">
            <div>Target price: <span className="font-mono text-white">{money(econ.target_retail)}</span> → build must cost <span className="font-mono text-white">{money(econ.build_ceiling_low)}–{money(econ.build_ceiling_high)}</span> ({econ.multiplier_rule})</div>
            <div>Current build cost: <span className={`font-mono ${econ.within_ceiling ? 'text-emerald-300' : 'text-red-300'}`} data-testid="current-build-cost">{money(econ.current_build_cost)}</span> <span className="text-zinc-500">({econ.cost_basis})</span></div>
            <div className={econ.within_ceiling ? 'text-emerald-300' : 'text-red-300'}>
              {econ.within_ceiling ? `✓ Inside the ceiling with ${money(econ.headroom_vs_high_ceiling)} headroom` : `Over the ceiling by ${money(-econ.headroom_vs_high_ceiling)} — cost work needed`}
            </div>
          </div>
        </Section>
      )}

      {/* COMPETITORS */}
      {comp && (
        <Section icon={TrendingDown} title="Who else is out there" testId="competitors">
          <div className="space-y-1.5">
            {comp.products.map((c) => (
              <div key={c.name} className="text-[12px] text-zinc-300">
                <span className="text-white">{c.name}</span> — {c.price} · {c.features} · <span className="text-amber-300/90">weak spot: {c.weakness}</span>
              </div>
            ))}
          </div>
          <div className="text-[12px] text-emerald-300 mt-2" data-testid="market-gap">{comp.market_gap}</div>
        </Section>
      )}

      {/* WAITLIST */}
      {wl && (
        <Section icon={ClipboardList} title={`Waitlist page ${wl.status === 'approved' ? '· approved' : '· awaiting your approval'}`} testId="waitlist">
          <div className="text-[12px] space-y-1">
            <div className="text-white text-base font-semibold">{wl.copy.headline}</div>
            <div className="text-zinc-300">{wl.copy.subhead}</div>
            {wl.copy.bullets.map((b, i) => <div key={i} className="text-zinc-400">• {b}</div>)}
            <div className="text-red-300/90 font-mono uppercase text-[11px] pt-1">{wl.copy.cta}</div>
            <div className="text-zinc-600 text-[11px]">{wl.copy.footer}</div>
          </div>
          <div className="text-[11px] text-amber-300/90 mt-2">{wl.note}</div>
          {wl.status !== 'approved' && (
            <button data-testid="approve-waitlist-btn"
              onClick={() => post('waitlist/approve', {}, 'Copy approved — stays private until the disclosure hold lifts')}
              className="mt-2 text-[11px] font-mono uppercase px-3 py-1.5 rounded bg-emerald-500/15 text-emerald-300 hover:bg-emerald-500/25">
              Approve this copy
            </button>
          )}
        </Section>
      )}

      {/* KILL CRITERIA */}
      {kc && (
        <Section icon={Target} title="Stop / pivot conditions" testId="kill-criteria">
          <div className="text-[10px] font-mono uppercase text-zinc-500 mb-1">{kc.status === 'approved' ? 'Approved' : 'Awaiting your approval'}</div>
          {kc.proposed.map((k, i) => <div key={i} className="text-[12px] text-zinc-300">• {k}</div>)}
          {kc.status !== 'approved' && (
            <button data-testid="approve-kill-criteria-btn"
              onClick={() => post('kill-criteria/approve', {}, 'Stop/pivot conditions locked in')}
              className="mt-2 text-[11px] font-mono uppercase px-3 py-1.5 rounded bg-white/10 text-white hover:bg-white/20">
              Approve these conditions
            </button>
          )}
        </Section>
      )}

      <div className="text-[11px] text-zinc-600">{view.email_note}</div>
    </div>
  );
};

export default SimpleVenture;
